import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppBranding } from '../../../../config/branding';
import DefaultApiLimit from '../../../../config/defaultLimit';
import {
  AvailableRevenueStationReport,
  RequestReportInterFace,
} from '../../../../interface/reportrevenueStation.interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationDetails } from '../../../../redux/slice/NotificationSlice';
import {
  fetchRevenueReportStation,
  setReportFilterData,
  updateReportFilters,
  setReportFiltersData,
  fetchListFilters,
  setSortData,
  setRevenueStationReportUpdate,
} from '../../../../redux/slice/ReportRevenueStationSlice';
import { resetReportUpdateFlag } from '../../../../redux/slice/ReportSlice';
import { RootState } from '../../../../redux/store';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import StationViewSidePanel from '../../../../shared-components/table-side-panel/StationViewSidePanel';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../../shared-functions/ConvertUTCtoDate';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import ToFixedValue from '../../../../shared-functions/ToFixedValue';
import reportApi from '../../../../utils/api/report';

interface ReportTableProperties {
  leftTopContent?: JSX.Element;
  preferenceFilter?: any;
  clearDateRange?: () => void;
}

/**
 * Revenue Report Table for Station
 */
function RevenueReportTableStation(properties: ReportTableProperties) {
  const { leftTopContent, preferenceFilter, clearDateRange } = properties;

  const dispatch = useAppDispatch();
  const { t } = useTranslation('reports');
  const branding = useAppBranding();
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const {
    sortBy,
    sortField,
    reports,
    limit,
    page,
    loading,
    totalPages,
    totalResults,
    filters,
    filterListLoading,
    isRevenueStationReportUpdate,
    isFilterUpdated,
  } = useAppSelector((state: RootState) => state.reportRevenueStation);
  const { currentFacilityTagsList } = useAppSelector((state: RootState) => state.facilityTags);
  const reportsData = useAppSelector((state: RootState) => state.reportData);

  const column = [
    {
      label: t('stationInfo'),
      accessorKey: 'chargingStationName' as keyof AvailableRevenueStationReport,
      /**
       * S.No view
       * @param station
       * @param index
       */
      customData: (report: AvailableRevenueStationReport) => (
        <div className="table-row-each-data">
          <div className="station-info-left">
            <span role="button" tabIndex={0}>
              {report.chargingStationName}
            </span>
            <span>{report.date}</span>
          </div>
          <div className="station-info-right">
            <span role="button" tabIndex={0}>
              {report.chargingStationLocation}
            </span>
          </div>
        </div>
      ),
      queryParamsKey: 'stationId',
      queryParamsValue: 'chargingStation',
      sidePanelComponent: <StationViewSidePanel fetchIdFrom="queryParams" />,
      sortAllow: false,
      columnWidth: 204,
    },

    {
      label: t('noOfDevice'),
      accessorKey: 'deviceCount' as keyof AvailableRevenueStationReport,
      sortAllow: false,
    },
    {
      label: t('totalRevenue'),
      accessorKey: 'totalRevenue' as keyof AvailableRevenueStationReport,
      sortAllow: true,
      /**
       * Formats the total revenue value with two decimal places.
       *
       * @param rowData - The current row's revenue data.
       * @returns string - The formatted revenue value.
       */
      customData: (rowData: AvailableRevenueStationReport) => (
        <div className="amount-in-transaction-table">
          <p className="table-row-each-data">
            {ToFixedValue(rowData?.totalRevenue) || '0.00'}
          </p>
          <span>{branding.text.currency}</span>
        </div>
      ),
    },
    {
      label: t('basePricingRevenue'),
      accessorKey: 'basePriceRevenue' as keyof AvailableRevenueStationReport,
      sortAllow: true,
      /**
       * Formats the base price revenue value with two decimal places.
       * @param rowData - The current row's revenue data.
       * @returns string - The formatted base price revenue value.
       */
      customData: (rowData: AvailableRevenueStationReport) => (
        <div className="amount-in-transaction-table">
          <p className="table-row-each-data">
            {ToFixedValue(rowData?.basePriceRevenue) || '0.00'}
          </p>
          <span>{branding.text.currency}</span>
        </div>
      ),
    },
    {
      label: t('peakPricingRevenue'),
      accessorKey: 'peakPriceRevenue' as keyof AvailableRevenueStationReport,
      sortAllow: true,
      /**
       * Formats the peak price revenue value with two decimal places.
       * @param rowData - The current row's revenue data.
       * @returns string - The formatted peak price revenue value.
       */
      customData: (rowData: AvailableRevenueStationReport) => (
        <div className="amount-in-transaction-table">
          <p className="table-row-each-data">
            {ToFixedValue(rowData?.peakPriceRevenue) || '0.00'}
          </p>
          <span>{branding.text.currency}</span>
        </div>
      ),
    },
    {
      label: t('offPeakPricingRevenue'),
      accessorKey: 'offPeakPriceRevenue' as keyof AvailableRevenueStationReport,
      sortAllow: true,
      /**
       * Formats the off-peak price revenue value with two decimal places.
       * @param rowData - The current row's revenue data.
       * @returns string - The formatted off-peak price revenue value.
       */
      customData: (rowData: AvailableRevenueStationReport) => (
        <div className="amount-in-transaction-table">
          <p className="table-row-each-data">
            {ToFixedValue(rowData?.offPeakPriceRevenue) || '0.00'}
          </p>
          <span>{branding.text.currency}</span>
        </div>
      ),
    },
    {
      label: t('scheduleChargeRevenue'),
      accessorKey: 'scheduleChargeRevenue' as keyof AvailableRevenueStationReport,
      sortAllow: true,
    },
  ];

  /**
   * Function to handle page change
   * @param pageNumber
   */
  const handlePageChange = (pageCount: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      fetchRevenueReportStation({
        ...queryParameters,
        sortField,
        sortBy,
        startDate: reportsData.reportFilter.startDate,
        endDate: reportsData.reportFilter.endDate,
        page: pageCount,
        limit,
      }),
    );
  };

  /**
   *  Handle the filter data clear function
   * @param filterData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      setReportFilterData({
        ...sortData,
      }),
    );
    dispatch(setSortData(sortData));
    dispatch(
      fetchRevenueReportStation({
        ...queryParameters,
        sortBy: sortData.sortBy,
        sortField: sortData.sortField,
        startDate: reportsData.reportFilter.startDate,
        endDate: reportsData.reportFilter.endDate,
        page: DefaultApiLimit.page,
        limit,
      }),
    );
  };

  /**
   * Function to handle data limit change
   */
  const handleLimitChange = (limitCount: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      fetchRevenueReportStation({
        ...queryParameters,
        startDate: reportsData.reportFilter.startDate,
        endDate: reportsData.reportFilter.endDate,
        page: DefaultApiLimit.page,
        limit: limitCount,
        sortField,
        sortBy,
      }),
    );
  };

  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('stationInfo'), accessorKey: 'chargingStationName' },
    { label: t('noOfDevice'), accessorKey: 'deviceCount' },
    { label: t('totalRevenue'), accessorKey: 'totalRevenue' },
    { label: t('basePricingRevenue'), accessorKey: 'basePriceRevenue' },
    { label: t('peakPricingRevenue'), accessorKey: 'peakPriceRevenue' },
    { label: t('offPeakPricingRevenue'), accessorKey: 'offPeakPriceRevenue' },
    { label: t('scheduleChargeRevenue'), accessorKey: 'scheduleChargeRevenue' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    const queryParameters = GetParametersFromCommonFilters(filters);
    const exportParameters = {
      ...queryParameters,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
      sortField,
      sortBy,
    };
    reportApi
      .fetchRevenueStationListExport({ ...exportParameters })
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map((row: AvailableRevenueStationReport, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;
              case 'chargingStationName':
                return `${row.chargingStationName} ${ConvertUTCtoDate(row.date)} ${row?.chargingStationLocation ?? ''}`;
              case 'deviceCount':
                return row?.deviceCount ?? 0;
              case 'totalRevenue':
                return `${ToFixedValue(row.totalRevenue ?? 0)} ${branding.text.currency}`;
              case 'basePriceRevenue':
                return `${ToFixedValue(row.basePriceRevenue ?? 0)} ${branding.text.currency}`;
              case 'peakPriceRevenue':
                return `${ToFixedValue(row.peakPriceRevenue ?? 0)} ${branding.text.currency}`;
              case 'offPeakPriceRevenue':
                return `${ToFixedValue(row.offPeakPriceRevenue ?? 0)} ${branding.text.currency}`;
              case 'scheduleChargeRevenue':
                return row.scheduleChargeRevenue ?? 0;
              default:
                return (row as any)[col?.accessorKey] || 'N/A';
            }
          }),
        );
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, `${t('reportsList')}.csv`);
          } else {
            exportPDF(headers, rows, `${t('reportsList')}.pdf`);
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };

  /**
   * Reference Current Pages
   */
  const ReferenceCurrentPages = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      fetchRevenueReportStation({
        ...queryParameters,
        sortBy,
        sortField,
        startDate: reportsData.reportFilter.startDate,
        endDate: reportsData.reportFilter.endDate,
        year: reportsData.reportFilter.year,
        page,
        limit,
      }),
    );
  };
  /**
   * Fetches energy report data for the selected facility.
   * @param data - The request parameters for fetching the report.
   */
  const fetchData = (data: RequestReportInterFace) => {
    const selectedFacility = currentFacilityTagsList.find((item) => item.isSelected);

    if (selectedFacility?._id && !loading) {
      dispatch(
        fetchRevenueReportStation({
          ...data,
        }),
      );
    }
  };

  const fetchReportData = useCallback(() => {
    const selectedFacility = currentFacilityTagsList.find((item) => item.isSelected);
    if (!selectedFacility?._id) return;

    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      fetchRevenueReportStation({
        ...queryParameters,
        startDate: reportsData.reportFilter.startDate,
        endDate: reportsData.reportFilter.endDate,
        year: reportsData.reportFilter.year,
        page: DefaultApiLimit.page,
        limit: DefaultApiLimit.limit,
      }),
    );
  }, [
    currentFacilityTagsList,
    filters,
    reportsData.reportFilter.startDate,
    reportsData.reportFilter.endDate,
    reportsData.reportFilter.year,
  ]);

  useEffect(() => {
    if (isFilterUpdated || reports.length === 0 || isRevenueStationReportUpdate) {
      fetchReportData();
      dispatch(resetReportUpdateFlag());
      dispatch(setRevenueStationReportUpdate(false));
    }
  }, [fetchReportData, isFilterUpdated, reports.length, isRevenueStationReportUpdate]);

  useEffect(() => {
    if (filters.length === 1) {
      dispatch(fetchListFilters());
    }

    if (preferenceFilter.pageName === 'revenueReport' && reportsData.reportByType === 'station') {
      if (filters.length > 1) {
        const setFilter = filters.map((filter) => {
          let returnValue = filter;
          const single = preferenceFilter.filter?.find(
            (preference: any) => preference.field === filter.key,
          );
          if (single && single?.value.length > 0) {
            returnValue = {
              ...returnValue,
              value: single?.value,
              applyFilter: true,
              valueToShow: returnValue?.listOfValue
                ?.filter((show) => single?.value?.includes(show.value))
                .map((show) => show.label),
            };
          } else {
            returnValue = {
              ...returnValue,
              value: [],
              applyFilter: false,
              valueToShow: [],
            };
          }
          return returnValue;
        });
        dispatch(setReportFiltersData(setFilter));
        const queryParameters = GetParametersFromCommonFilters(setFilter);
        fetchData({
          ...queryParameters,
          startDate: reportsData.reportFilter.startDate,
          endDate: reportsData.reportFilter.endDate,
          page: DefaultApiLimit.page,
          limit,
        });
      }
    }
  }, [preferenceFilter]);

  return (
    <div className="report-management-wrap">
      <div className="table-container">
        <CustomTable
          paginate
          loading={loading}
          limit={limit}
          header={column}
          data={reports}
          currentPage={page}
          totalPages={totalPages}
          totalResults={totalResults}
          sortData={{
            sortBy,
            sortField,
          }}
          handleSortChange={handleSortFunction}
          handlePageChange={handlePageChange}
          handleLimitChange={handleLimitChange}
          tableHeight="calc(100vh - 335px)"
          version="v2"
          exportDataPdfOrCSV={exportDataPdfOrCSV}
          exportData={reports}
          exportLoader={exportLoader}
          topHeader={false}
          filters={filters}
          filtersToDispatch={updateReportFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchListFilters}
          rightTopContent={leftTopContent}
          refreshCurrentPages={ReferenceCurrentPages}
          serialNo
          onClearFilterChange={() => {
            if (clearDateRange) {
              clearDateRange();
            }
          }}
          onHiddenColumnsChange={setHiddenColumns}
          flexDirection
        />
      </div>
    </div>
  );
}

export default RevenueReportTableStation;
